import type {
  FormerMember,
  HouseholdRelation,
  Lookups,
  Person,
  ProfileMe,
  Relationship,
  RelationshipLabel,
} from '@/lib/types';
import { relationshipLabel } from './RelationshipPage';

/** Everyone in the household, me first. */
export function household(d: ProfileMe): Person[] {
  return [d.me, ...d.members];
}

/**
 * Former household members to list under this person's Elsewhere: those they
 * have no relationship row with. A relationship, when there is one, is the
 * better description, so it wins and the person is listed once.
 */
export function formerElsewhere(d: ProfileMe, person: Person): FormerMember[] {
  const related = new Set(person.family.map((f) => f.contactId));
  const inHouse = new Set(household(d).map((p) => p.contactId));
  return (d.formerMembers ?? []).filter(
    (f) => !related.has(f.contactId) && !inHouse.has(f.contactId)
  );
}

/** What `other` is to `person`, from the person's own rows; null when never written down. */
export function relationOf(person: Person, otherId: number): RelationshipLabel | null {
  return person.relations.find((r) => r.contactId === otherId)?.relationship ?? null;
}

export function findRelationship(lookups: Lookups, id: number) {
  for (const t of lookups.relationshipTypes) {
    const r = t.relationships.find((x) => x.id === id);
    if (r) return { r, t };
  }
  return null;
}

function mapPerson(d: ProfileMe, contactId: number, fn: (p: Person) => Person): ProfileMe {
  const next: ProfileMe = {
    ...d,
    me: d.me.contactId === contactId ? { ...d.me, ...fn(d.me) } : d.me,
    members: d.members.map((m) => (m.contactId === contactId ? fn(m) : m)),
  };
  return next;
}

function withRelation(p: Person, rel: HouseholdRelation | null, otherId: number): Person {
  const rest = p.relations.filter((r) => r.contactId !== otherId);
  return { ...p, relations: rel ? [...rest, rel] : rest };
}

/** `members[].relationship` is "what they are to me": keep it in step with my rows. */
function syncMine(d: ProfileMe): ProfileMe {
  return {
    ...d,
    members: d.members.map((m) => ({ ...m, relationship: relationOf(d.me, m.contactId) })),
  };
}

/**
 * Write "`other` is `rel` of `subject`" into the page's data, both ways:
 * the subject's row and the other person's mirror (the reciprocal, the way
 * MP stores a relationship as two rows). `rel` null ends the pair.
 */
export function applyRelation(
  d: ProfileMe,
  subject: Person,
  other: Person,
  rel: Relationship | null,
  contactRelationshipId: number
): ProfileMe {
  const { lookups } = d;
  let next = d;
  if (!rel) {
    next = mapPerson(next, subject.contactId, (p) => withRelation(p, null, other.contactId));
    next = mapPerson(next, other.contactId, (p) => withRelation(p, null, subject.contactId));
    return syncMine(next);
  }
  const found = findRelationship(lookups, rel.id);
  const mirror =
    (rel.reciprocalId !== null ? findRelationship(lookups, rel.reciprocalId) : null) ?? found;
  next = mapPerson(next, subject.contactId, (p) =>
    withRelation(
      p,
      {
        contactId: other.contactId,
        relationship: {
          contactRelationshipId,
          relationshipId: rel.id,
          label: relationshipLabel(rel, other.genderId),
          typeId: found?.t.id ?? null,
          typeName: found?.t.name ?? null,
        },
      },
      other.contactId
    )
  );
  if (mirror) {
    next = mapPerson(next, other.contactId, (p) =>
      withRelation(
        p,
        {
          contactId: subject.contactId,
          relationship: {
            contactRelationshipId,
            relationshipId: mirror.r.id,
            label: relationshipLabel(mirror.r, subject.genderId),
            typeId: mirror.t.id,
            typeName: mirror.t.name,
          },
        },
        subject.contactId
      )
    );
  }
  return syncMine(next);
}

/** Swap a temporary row id for the one MP handed back, wherever it landed. */
export function replaceRelationId(d: ProfileMe, tempId: number, realId: number): ProfileMe {
  const fix = (p: Person): Person => ({
    ...p,
    relations: p.relations.map((r) =>
      r.relationship.contactRelationshipId === tempId
        ? { ...r, relationship: { ...r.relationship, contactRelationshipId: realId } }
        : r
    ),
    relationship:
      p.relationship?.contactRelationshipId === tempId
        ? { ...p.relationship, contactRelationshipId: realId }
        : p.relationship,
  });
  return { ...d, me: { ...d.me, ...fix(d.me) }, members: d.members.map(fix) };
}
